import { useTranslations } from "@src/packages/i18n";
import type { PlanAction, PlanStep } from "../plan.types";
import { PreCycleHome, type PreCycleHomeSection } from "./PreCycleHome";
import type { PreCyclePrepStep } from "./PreCycleHeroCard";
import { EggFreezingClinicTimelineStep } from "./EggFreezingPrepTimelineRemainingSteps";
import { SpermAnalysisSkeletonCard } from "./SpermAnalysisSkeletonCard";
import { UnderstandSpermFreezingStep } from "./SpermFreezingUnderstandStep";
import { KnowBaselineStep } from "./SpermFreezingKnowBaselineStep";
import { SpermResetProgramStep } from "./SpermFreezingResetStep";
import { SpermFreezingStartStep } from "./SpermFreezingStartStep";

type Props = {
  activeStep: PlanStep | null;
  steps: PreCyclePrepStep[];
  completedSteps: number;
  totalSteps: number;
  userAge?: number;
  onAction: (action: PlanAction) => void;
  onOpenPlan: () => void;
  onEditPlan: () => void;
};

export function PreCycleSpermFreezingHome({
  activeStep,
  steps,
  completedSteps,
  totalSteps,
  onAction,
  onOpenPlan,
  onEditPlan,
}: Props) {
  const t = useTranslations();
  const heroContent = {
    eyebrow: t("homepage.pre_cycle_sperm_freezing.eyebrow"),
    title: t("homepage.pre_cycle_sperm_freezing.title"),
    cycleStartLabel: t("homepage.pre_cycle_sperm_freezing.cycle_start_label"),
    phaseDescription: t("homepage.pre_cycle_sperm_freezing.phase_description"),
    intro: t("homepage.pre_cycle_sperm_freezing.intro"),
    openPlanAccessibilityLabel: t(
      "homepage.pre_cycle_sperm_freezing.open_plan_a11y",
    ),
    planAccessibilityLabel: t(
      "homepage.pre_cycle_sperm_freezing.toggle_plan_a11y",
    ),
  };
  const sections: PreCycleHomeSection[] = [
    {
      id: "understand-sperm-freezing",
      progressStepIds: ["learn"],
      render: () => <UnderstandSpermFreezingStep onAction={onAction} />,
    },
    {
      id: "know-sperm-baseline",
      progressStepIds: ["test"],
      render: () => <KnowBaselineStep onAction={onAction} />,
    },
    {
      id: "sperm-reset-program",
      progressStepIds: [],
      render: () => <SpermResetProgramStep onAction={onAction} />,
    },
    {
      id: "find-sperm-care",
      progressStepIds: [],
      render: () => (
        <EggFreezingClinicTimelineStep
          onAction={onAction}
          variant="sperm-freezing"
        />
      ),
    },
    {
      id: "sperm-analysis-skeleton",
      includeInPlanSetup: false,
      progressStepIds: [],
      render: () => (
        <SpermAnalysisSkeletonCard onAction={onAction} layout="single" />
      ),
    },
    {
      id: "start-sperm-banking",
      includeInPlanSetup: false,
      progressStepIds: ["freeze"],
      render: () => <SpermFreezingStartStep onAction={onAction} />,
    },
  ];

  return (
    <PreCycleHome
      activeStep={activeStep}
      steps={steps}
      completedSteps={completedSteps}
      totalSteps={totalSteps}
      eyebrow={heroContent.eyebrow}
      title={heroContent.title}
      snapshotItems={[
        {
          label: t("homepage.pre_cycle_egg_freezing.next"),
          value:
            activeStep?.title ??
            t("homepage.pre_cycle_egg_freezing.choose_next_step"),
        },
        {
          label: t("homepage.pre_cycle_sperm_freezing.reset_window"),
          value: t("homepage.pre_cycle_sperm_freezing.reset_window_value"),
        },
      ]}
      phaseDescription={heroContent.phaseDescription}
      intro={heroContent.intro}
      openPlanAccessibilityLabel={heroContent.openPlanAccessibilityLabel}
      planAccessibilityLabel={heroContent.planAccessibilityLabel}
      onAction={onAction}
      onOpenPlan={onOpenPlan}
      onEditPlan={onEditPlan}
      showStartCycleAction
      startCyclePreset="sperm_freezing"
      sections={sections}
    />
  );
}
